import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/lib/testIds";
import { ArrowUpRight } from "lucide-react";

const IMAGES = [
  "https://images.unsplash.com/photo-1523477593243-78bbf626fd3b?auto=format&fit=crop&w=1600&q=80", // glass facade
  "https://images.pexels.com/photos/37320179/pexels-photo-37320179.jpeg?auto=compress&cs=tinysrgb&w=1600", // curtain wall
  "https://images.unsplash.com/photo-1531973576160-7125cd663d86?auto=format&fit=crop&w=1600&q=80", // aluminium joinery
  "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1600&q=80", // balustrade
  "https://images.unsplash.com/photo-1505409859467-3a796fd5798e?auto=format&fit=crop&w=1600&q=80", // interior partition
  "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80", // special glass
  "/assets/accesorii.jpg", // accessories — provided by client
];

export default function SolutionsSection() {
  const { t } = useI18n();
  const items = t("solutions.items");

  return (
    <section
      data-testid={TID.solutions}
      className="relative bg-white py-24 md:py-32"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-3xl">
            <p className="gc-eyebrow">{t("solutions.eyebrow")}</p>
            <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-[700] text-[color:var(--gc-ink)]">
              {t("solutions.title")}
            </h2>
            <p className="mt-5 text-base md:text-lg text-[color:var(--gc-steel)] max-w-xl leading-relaxed">
              {t("solutions.lead")}
            </p>
          </div>
          <Link
            to="/solutii"
            className="gc-link text-sm uppercase tracking-[0.14em] font-medium text-[color:var(--gc-ink)]"
          >
            <span>{t("common.cta_solutions")}</span>
            <ArrowUpRight className="w-4 h-4 ml-2" />
          </Link>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[color:var(--gc-line)] border border-[color:var(--gc-line)]">
          {Array.isArray(items) &&
            items.map((it, i) => (
              <motion.article
                key={it.k}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
                className={`relative bg-white group ${
                  i === 0 ? "md:col-span-2 lg:col-span-2 lg:row-span-2" : ""
                }`}
              >
                <div className={`relative overflow-hidden ${i === 0 ? "h-72 lg:h-[440px]" : "h-44 lg:h-56"}`}>
                  <img
                    src={IMAGES[i % IMAGES.length]}
                    alt=""
                    className="w-full h-full object-cover grayscale-[15%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--gc-navy)]/60 via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 font-mono text-[11px] tracking-[0.18em] text-white/80">
                    {it.k}
                  </span>
                </div>
                <div className="p-7 lg:p-8">
                  <h3 className="font-display font-[700] text-xl lg:text-2xl leading-tight text-[color:var(--gc-ink)] tracking-tight">
                    {it.t}
                  </h3>
                  <p className="mt-3 text-sm lg:text-[15px] leading-relaxed text-[color:var(--gc-steel)]">
                    {it.d}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.14em] text-[color:var(--gc-ink)]">
                    <span className="w-6 h-px bg-[color:var(--gc-red)]" />
                    <Link to="/solutii" className="hover:underline">
                      {t("common.cta_discuss")}
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
        </div>
      </div>
    </section>
  );
}
